import * as THREE from 'three'
import { gsap } from 'gsap'
import Experience from '../../Experience'
import { InteractableMixin } from '../../Utils/InteractableMixin'

/**
 * Lantern — 流れ星ランタン。惑星表面に置かれた小さなランタン。
 * クリックで ON/OFF をトグルする:
 *   ON  → 灯りが強まり、少し遅れて流星群（ShootingStars のバーストモード）が
 *         夜空に流れ続ける
 *   OFF → すべて静かに収束して元の淡いゆらめきに戻る
 *
 * プロップはプロシージャルなプレースホルダー（Group 構成）。
 * 後で GLTF に差し替える場合は setModel() の中身を置き換えるだけでよい。
 */
const PLANET_CENTER = new THREE.Vector3(-4, -4, 16)
const BURST_COLOR = '#bcd4ff' // 天体ゾーンの金色 #ffe08a と区別する青白
const BURST_DELAY = 2.0       // 点灯から流星が流れ始めるまでの間（灯りが強まってから空が応える演出）

export default class Lantern {
  constructor() {
    this.experience = new Experience()
    this.scene = this.experience.scene
    this.time = this.experience.time
    this.quality = this.experience.quality
    this.debug = this.experience.debug
    this.camera = this.experience.camera

    this.isOn = false
    this.baseIntensity = 0.35
    this.currentGlow = 0.0 // 0=消灯時 → 4=点灯時（GSAPでtween）
    this.burstOwned = false
    this.prevSpawnEnabled = true
    this.burstDelayCall = null
    this.scratchDir = new THREE.Vector3() // computeSkyDirection 用

    this.setModel()
    this.setInteraction()
    this.setDebug()

    this.time.on('tick', () => {
      this.update()
    })
  }

  setModel() {
    this.group = new THREE.Group()

    const frameMaterial = new THREE.MeshStandardMaterial({ color: '#3a2f28', roughness: 0.9 })

    // 四隅の柱
    const postGeometry = new THREE.BoxGeometry(0.03, 0.3, 0.03)
    for (const x of [-0.09, 0.09]) {
      for (const z of [-0.09, 0.09]) {
        const post = new THREE.Mesh(postGeometry, frameMaterial)
        post.position.set(x, 0.19, z)
        this.group.add(post)
      }
    }

    // 台座と笠
    const base = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.05, 0.24), frameMaterial)
    base.position.y = 0.025
    this.group.add(base)

    const roof = new THREE.Mesh(new THREE.ConeGeometry(0.19, 0.12, 4), frameMaterial)
    roof.position.y = 0.4
    roof.rotation.y = Math.PI / 4
    this.group.add(roof)

    // 炎コア（自己発光。flicker でスケール脈動）
    this.flameCore = new THREE.Mesh(
      new THREE.SphereGeometry(0.06, 12, 12),
      new THREE.MeshBasicMaterial({ color: '#ffb46b' })
    )
    this.flameCore.position.y = 0.19
    this.group.add(this.flameCore)

    this.light = new THREE.PointLight('#ffb46b', this.baseIntensity, 4, 2)
    this.light.position.y = 0.22
    this.group.add(this.light)

    // レイキャスト用の不可視ヒットスフィア（InteractionManager は this.mesh のみ判定する）
    this.mesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.45, 8, 8),
      new THREE.MeshBasicMaterial({ visible: false })
    )
    this.mesh.position.y = 0.2
    this.group.add(this.mesh)

    // 惑星表面に配置し、+Y を表面法線に合わせる
    this.setSurfacePosition(new THREE.Vector3(-1.8, -2.4, 17.3))
    this.scene.add(this.group)
  }

  // 位置から惑星中心→位置 の向きを計算して姿勢を合わせる（GUI 調整でも使う）
  setSurfacePosition(position) {
    this.group.position.copy(position)
    const normal = position.clone().sub(PLANET_CENTER).normalize()
    this.group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal)
  }

  setInteraction() {
    Object.assign(this, InteractableMixin)

    this.onInteract = () => {
      this.toggle()
    }

    this.makeInteractable()
  }

  toggle() {
    // イントロのカメラアニメーション中は無視
    if (this.camera.animationState.isAnimating) return

    if (this.isOn) {
      this.turnOff()
    } else {
      this.turnOn()
    }
  }

  turnOn() {
    this.isOn = true
    this.experience.audioManager?.playSE('on_sound_0', 0.5, 1.0)

    // 灯りをふわっと強める
    gsap.to(this, { currentGlow: 4.0, duration: 1.2, ease: 'power2.out', overwrite: true })

    // reduced-motion: 流星が動かないので灯りだけ
    if (this.quality.reducedMotion) return

    // 灯りが強まった頃、空が応える — 流星群
    this.burstDelayCall?.kill()
    this.burstDelayCall = gsap.delayedCall(BURST_DELAY, () => {
      if (this.isOn) this.startBurst()
    })
  }

  turnOff() {
    this.isOn = false
    this.experience.audioManager?.playSE('off_sound_0', 0.5, 1.0)

    this.burstDelayCall?.kill()
    this.burstDelayCall = null

    this.stopBurst()
    gsap.to(this, { currentGlow: 0.0, duration: 1.5, ease: 'power2.inOut', overwrite: true })
  }

  /**
   * カメラの視線の先(40ユニット)にある点を、天球中心(原点)から見た方向にする。
   * ＝「今カメラが見ている画面中央付近の空」。天球(r=25)へのレイ交差計算より
   * 単純で、カメラがどこを向いていても破綻しない。
   */
  computeSkyDirection(target) {
    const cameraInstance = this.camera.instance
    cameraInstance.getWorldDirection(target)
    return target.multiplyScalar(40).add(cameraInstance.position).normalize()
  }

  startBurst() {
    const shootingStars = this.experience.world?.shootingStars
    if (!shootingStars || shootingStars.burstActive) return

    // works セクションでは spawn が止められているので、点灯中だけ有効化して後で戻す
    this.prevSpawnEnabled = shootingStars.enabled
    shootingStars.setEnabled(true)
    // 固定の空域ではなく「今見ている空」へ流す（画面外で流れて見えない問題の対策）
    shootingStars.setBurst(true, BURST_COLOR, this.computeSkyDirection(this.scratchDir))
    this.burstOwned = true
  }

  stopBurst() {
    const shootingStars = this.experience.world?.shootingStars
    if (!shootingStars || !this.burstOwned) return

    shootingStars.setBurst(false)
    shootingStars.setEnabled(this.prevSpawnEnabled)
    this.burstOwned = false
  }

  update() {
    const shootingStars = this.experience.world?.shootingStars

    if (this.burstOwned && shootingStars) {
      // カメラの動き（ドラッグ・セクションスクロール）に追従して
      // 流星の出現方向を更新し続け、常に画面内の空へ流す
      shootingStars.setBurstDirection(this.computeSkyDirection(this.scratchDir))

      // 点灯が長引く間に天体ゾーン（saturn）や SceneDirector が状態を触っても自己修復する
      if (this.isOn) {
        if (!shootingStars.enabled) shootingStars.setEnabled(true)
        if (!shootingStars.burstActive) {
          shootingStars.setBurst(true, BURST_COLOR, this.computeSkyDirection(this.scratchDir))
        }
      }
    }

    if (this.quality.reducedMotion) {
      this.light.intensity = this.currentGlow * this.baseIntensity
      return
    }

    // 位相の異なる正弦波を重ねた疑似ノイズのゆらめき
    const t = this.time.elapsed
    const flicker = 0.75 + 0.25 * Math.sin(t * 7.3) * Math.sin(t * 3.1 + 1.7)
    this.light.intensity = this.currentGlow * this.baseIntensity * flicker
    this.flameCore.scale.setScalar((0.9 + 0.15 * flicker) * Math.min(this.currentGlow, 2.0))
  }

  setDebug() {
    if (!this.debug.active) return

    this.debugFolder = this.debug.ui.addFolder('Lantern')
    const apply = () => this.setSurfacePosition(this.group.position)
    this.debugFolder.add(this.group.position, 'x').min(-10).max(5).step(0.05).name('Position X').onChange(apply)
    this.debugFolder.add(this.group.position, 'y').min(-8).max(2).step(0.05).name('Position Y').onChange(apply)
    this.debugFolder.add(this.group.position, 'z').min(12).max(20).step(0.05).name('Position Z').onChange(apply)
    this.debugFolder.add(this.group.scale, 'x').min(0.2).max(3).step(0.05).name('Scale').onChange((value) => {
      this.group.scale.setScalar(value)
    })
    this.debugFolder.add(this.light, 'intensity').min(0).max(3).step(0.01).name('Light Intensity').listen()
    this.debugFolder.add({ toggle: () => this.toggle() }, 'toggle').name('🔥 Toggle On/Off')
    this.debugFolder.add(this, 'isOn').name('Is On').listen()
    this.debugFolder.close()
  }
}
